import { useSyncExternalStore } from "react";

/**
 * The new-order alert staff hear: a two-note chime, then the browser's own voice saying
 * "मिल्किट पे नया ऑर्डर आया है" (Hindi when the device has a Hindi voice, else English). Made in the
 * browser, so there is no audio file to load or license. Staff turn it off from the header.
 */

const SOUND_KEY = "milqit.order-sound";
const HINDI = "मिल्किट पे नया ऑर्डर आया है";
const ENGLISH = "New order on Milqit";

const listeners = new Set<() => void>();

/** Whether order sounds are on right now (for event handlers; components use the hook). */
export function isOrderSoundOn(): boolean {
  try {
    // On unless someone switched it off: a missed order costs more than a sound.
    return window.localStorage.getItem(SOUND_KEY) !== "off";
  } catch {
    return true;
  }
}

/** Whether order sounds are on, shared by every screen (and other tabs, via storage events). */
export function useOrderSoundOn(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      window.addEventListener("storage", onChange);
      return () => {
        listeners.delete(onChange);
        window.removeEventListener("storage", onChange);
      };
    },
    isOrderSoundOn,
    () => true,
  );
}

export function setOrderSoundOn(on: boolean) {
  try {
    window.localStorage.setItem(SOUND_KEY, on ? "on" : "off");
  } catch {
    // Storage blocked (private mode): the choice still holds until the page reloads.
  }
  for (const listener of listeners) listener();
}

/** Two short rising tones via Web Audio. */
export function playChime() {
  try {
    const context = new AudioContext();
    [880, 1320].forEach((frequency, index) => {
      const at = context.currentTime + index * 0.18;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.25, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(at);
      oscillator.stop(at + 0.17);
    });
    window.setTimeout(() => void context.close(), 600);
  } catch {
    // Audio unavailable, or blocked until the page has been clicked: the toast still shows.
  }
}

/** The best voice for the announcement: Hindi, else Indian English, else the device's default. */
function pickVoice(): SpeechSynthesisVoice | undefined {
  const voices = window.speechSynthesis.getVoices();
  return (
    voices.find((voice) => voice.lang.toLowerCase().startsWith("hi")) ??
    voices.find((voice) => voice.lang.toLowerCase() === "en-in")
  );
}

function speak() {
  if (!("speechSynthesis" in window)) return;
  // Several orders at once: one announcement is enough, the toasts list them all.
  if (window.speechSynthesis.speaking || window.speechSynthesis.pending) return;
  try {
    const voice = pickVoice();
    const hindi = voice?.lang.toLowerCase().startsWith("hi") ?? false;
    const utterance = new SpeechSynthesisUtterance(hindi ? HINDI : ENGLISH);
    if (voice) utterance.voice = voice;
    utterance.lang = hindi ? "hi-IN" : (voice?.lang ?? "en-IN");
    utterance.rate = 0.95;
    window.speechSynthesis.speak(utterance);
  } catch {
    // Speech unavailable on this browser: the chime has played and the toast still shows.
  }
}

/** Chrome fills its voice list in the background; asking early means it's ready for the first order. */
export function loadVoices() {
  if ("speechSynthesis" in window) window.speechSynthesis.getVoices();
}

/** The full alert: chime, then the spoken line once the chime has finished. */
export function announceNewOrder() {
  playChime();
  window.setTimeout(speak, 450);
}
